import type { DebriefMetricDef } from "./FleetDebriefStripTable";

/**
 * Metric rows for the Fleet Debrief — shared by the live FLEET SUMMARY page
 * (live/reports/LiveFleetSummary.tsx) and the print/PDF pop-out (pages/FleetDebrief.tsx) so the
 * two cannot drift apart.
 *
 * Keys are the lowercased row keys returned by /api/data/daily-training-settings (the client
 * lowercases them in normalizeDailySettingsRows). They must match the server's
 * STRAIGHT_LINE_METRICS / STRAIGHT_LINE_VARIATION_METRICS / MANEUVER_METRICS_TACK key sets in
 * server_app/services/dailyTrainingSettingsService.js — a key with no matching column simply
 * renders no row, silently, because buildRowModel returns null when no boat has a value.
 * fleetDebriefMetrics.test.ts pins that correspondence.
 *
 * `_std` / `_aav` keys come from the STD / AAV rows of events_aggregate rather than the AVG row:
 * STD is how much the channel varied inside each BIN 10 window, AAV how busily it varied
 * (units/s at 10 Hz). See docs/analysis/straight-line-aggregates.md.
 */
export const DEBRIEF_STRAIGHT_METRICS: DebriefMetricDef[] = [
  { group: "Boat States", key: "tws_avg", label: "TWS", unit: "kph", dp: 1 },
  { group: "Boat States", key: "bsp", label: "BSP", unit: "kph", dp: 1 },
  { group: "Boat States", key: "twa", label: "TWA", unit: "deg", dp: 1 },
  { group: "Boat States", key: "vmg_percent", label: "VMG", unit: "%", dp: 1 },
  { group: "Boat States", key: "heel_n", label: "Heel", unit: "deg", dp: 1 },
  { group: "Boat States", key: "pitch", label: "Pitch", unit: "deg", dp: 1 },
  { group: "Boat States", key: "rud_rake", label: "Rud Rake", unit: "deg", dp: 1 },

  { group: "Foiling", key: "rh_lwd", label: "RH Lwd", unit: "mm", dp: 0 },
  { group: "Foiling", key: "rh_lwd_std", label: "RH Lwd STD", unit: "mm", dp: 1 },
  { group: "Foiling", key: "db_piercing_lwd", label: "Piercing Lwd", unit: "mm", dp: 0 },
  { group: "Foiling", key: "db_cant", label: "Cant Lwd", unit: "deg", dp: 1 },
  { group: "Foiling", key: "db_cant_eff", label: "Cant Eff Lwd", unit: "deg", dp: 1 },

  { group: "Wing Trim", key: "wing_ca1", label: "CA1", unit: "deg", dp: 1 },
  { group: "Wing Trim", key: "wing_twist", label: "Twist", unit: "deg", dp: 1 },
  { group: "Wing Trim", key: "wing_twist_aav", label: "Twist AAV", unit: "deg/s", dp: 2 },
  { group: "Wing Trim", key: "wing_clew", label: "Clew", unit: "mm", dp: 0 },
  { group: "Wing Trim", key: "wing_clew_aav", label: "Clew AAV", unit: "mm/s", dp: 1 },
  { group: "Wing Trim", key: "wing_aoa", label: "Mast AOA", unit: "deg", dp: 1 },

  { group: "Jib", key: "jib_sht", label: "Sheet Load", unit: "kgf", dp: 0 },
  { group: "Jib", key: "jib_lead", label: "Car", unit: "deg", dp: 1 },
  { group: "Jib", key: "jib_lead_std", label: "Car STD", unit: "deg", dp: 2 },
  { group: "Jib", key: "jib_cunno", label: "Cunno Load", unit: "kgf", dp: 0 },
];

/**
 * Tack / gybe rows. The endpoint returns 21 maneuver metrics (MANEUVER_METRICS_TACK); these are
 * the six the debrief shows. Adding another is one line here.
 */
export const DEBRIEF_MANEUVER_METRICS: DebriefMetricDef[] = [
  { key: "bsp_entry", label: "Entry BSP", unit: "kph", dp: 1 },
  { key: "twa_entry", label: "Entry TWA", unit: "deg", dp: 1 },
  { key: "twa_exit", label: "Exit TWA", unit: "deg", dp: 1 },
  { key: "twa_accmax", label: "TWA at Max Acc", unit: "deg", dp: 1 },
  { key: "bsp_min", label: "Min BSP", unit: "kph", dp: 1 },
  { key: "turn_rate_max", label: "Max Turn Rate", unit: "deg/s", dp: 1 },
  { key: "drop_time", label: "Drop Time", unit: "s", dp: 1 },
  { key: "raise_time", label: "Raise Time", unit: "s", dp: 1 },
  { key: "vmg_perc_avg", label: "VMG", unit: "%", dp: 1 },
  { key: "loss_inv_tgt", label: "Inv Loss", unit: "m", dp: 1 },
  { key: "loss_turn_tgt", label: "Turn Loss", unit: "m", dp: 1 },
  { key: "loss_build_tgt", label: "Build Loss", unit: "m", dp: 1 },
  { key: "loss_total_tgt", label: "Total Loss", unit: "m", dp: 1 },
];

/** Channel groups in render order — used by tests and by anything that needs the group list. */
export const DEBRIEF_STRAIGHT_GROUPS: string[] = [
  ...new Set(DEBRIEF_STRAIGHT_METRICS.map((m) => m.group).filter((g): g is string => !!g)),
];
